import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import axios from "axios";

// Admin pop-up for one gig's client portal: create / copy / replace / turn off the
// private link. (Client side: ../ClientPortal.js)

const API_BASE_URL = "/api";

const portalUrl = (token) => `${window.location.origin}/portal/${token}`;

export default function GigPortalModal({ gig, onClose }) {
    const [portal, setPortal] = useState(null);
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const [copied, setCopied] = useState(false);
    const linkRef = useRef(null);

    const load = async () => {
        try {
            const res = await axios.get(`${API_BASE_URL}/Gigs/${gig.id}/portal`);
            setPortal(res.data);
            setError("");
        } catch (err) {
            console.error("PORTAL LOAD ERROR:", err.response || err);
            setError(err.response?.status === 500
                ? "Could not load the portal. Has Sql/ClientPortal.sql been run on the database?"
                : "Could not load the portal.");
        }
    };

    useEffect(() => { load(); }, [gig.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const createLink = async (replacing) => {
        if (replacing && !window.confirm("Make a new link? The current link will stop working right away.")) return;
        setBusy(true);
        try {
            await axios.post(`${API_BASE_URL}/Gigs/${gig.id}/portal`);
            await load();
        } catch (err) {
            console.error("PORTAL CREATE ERROR:", err.response || err);
            setError("Could not create the link.");
        } finally {
            setBusy(false);
        }
    };

    const turnOff = async () => {
        if (!window.confirm("Turn off this link? The client won't be able to open it.")) return;
        setBusy(true);
        try {
            await axios.delete(`${API_BASE_URL}/Gigs/${gig.id}/portal`);
            await load();
        } catch (err) {
            console.error("PORTAL REVOKE ERROR:", err.response || err);
            setError("Could not turn off the link.");
        } finally {
            setBusy(false);
        }
    };

    const copy = async () => {
        const url = portalUrl(portal.token);
        try {
            await navigator.clipboard.writeText(url);
        } catch {
            linkRef.current?.select(); // clipboard blocked: select it so Ctrl+C works
            document.execCommand?.("copy");
        }
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const emailHref = () => {
        const date = new Date(gig.startDateTime).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" });
        const subject = `Your event page: Dirty Yachtin' Scoundrels, ${date}`;
        const body =
            `Hi ${gig.clientName || "there"},\n\n` +
            `Here's your private page for your event on ${date}, where you can check ` +
            `the details, see your payments, and view your contract any time:\n\n` +
            `${portalUrl(portal.token)}\n\n` +
            `Please keep this link to yourself, since anyone with it can see the page.\n\n` +
            `See you on deck!\nDirty Yachtin' Scoundrels`;
        return `mailto:${encodeURIComponent(gig.clientEmail || "")}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    };

    return ReactDOM.createPortal(
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxHeight: "90vh", overflowY: "auto", color: "#1d2a38" }}>
                <h3>Client portal: {gig.clientName || gig.title || "Gig"}</h3>
                <p style={{ marginTop: 0, opacity: 0.75 }}>{new Date(gig.startDateTime).toLocaleString()} · {gig.venueName}</p>

                {error && <p style={{ color: "#b8405e" }}>{error}</p>}
                {!portal && !error && <p>Loading…</p>}

                {portal && (
                    <>
                        {portal.token ? (
                            <>
                                <label style={{ fontWeight: 600 }}>Private link for the client</label>
                                <input ref={linkRef} readOnly value={portalUrl(portal.token)}
                                    onFocus={(e) => e.target.select()} style={{ width: "100%", margin: "4px 0 8px" }} />
                                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                                    <button type="button" className="edit-btn" onClick={copy}>{copied ? "Copied!" : "Copy link"}</button>
                                    <a className="edit-btn" href={emailHref()} style={{ textDecoration: "none" }}>Email to client</a>
                                    <button type="button" className="view-btn" onClick={() => window.open(portalUrl(portal.token), "_blank", "noopener")}>Open</button>
                                    <button type="button" className="replace-btn" disabled={busy} onClick={() => createLink(true)}>New link</button>
                                    <button type="button" className="cancel-btn" disabled={busy} onClick={turnOff}>Turn off</button>
                                </div>
                                <p style={{ fontSize: "0.85em", opacity: 0.75 }}>
                                    The client sees the date, venue, fee, deposit and balance, and the contract PDF.
                                    Notes, private notes and client phone/email are never shown.
                                </p>
                            </>
                        ) : (
                            <>
                                <p>
                                    {portal.createdAt ? "The link is turned off." : "No client link yet."} Create one to send the client a private page
                                    with their event details, payments and contract.
                                </p>
                                <button type="button" className="edit-btn" disabled={busy} onClick={() => createLink(false)}>
                                    {portal.createdAt ? "Turn on with a new link" : "Create link"}
                                </button>
                            </>
                        )}
                    </>
                )}

                <div className="modal-buttons">
                    <button type="button" className="cancel-btn" onClick={onClose}>Close</button>
                </div>
            </div>
        </div>,
        document.body
    );
}
